import { FC, ReactNode } from 'react'
import clsx from 'clsx'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'

export const LISTING_PROFILE_TOOLTIPS = {
  profileName:
    'Give this listing profile a recognizable name so you can find and manage it later. If left blank, a name will be generated automatically.',

  productCriteria:
    'Define which products from your source store should use this profile. Add filters by field (such as SKU or tags) to include only matching products.',

  titleDescriptionSection:
    'Customize how product titles and descriptions are sent to the marketplace. Pass your default source text, merge extra catalog attributes, or map custom source fields to optimize your listings for search and conversions.',

  title:
    'Control the exact title format sent to your sales channels. You can combine the master product title with extra data (e.g., {TITLE} - Limited Edition), prepend brand names, or select a designated custom title field from your source store.',

  description:
    'Choose how descriptions are formatted and pushed to the marketplace. You can inherit your master store description, append custom warranty or shipping details at the bottom, or map directly to an alternate description field from your source inventory.',

  priceModifier:
    'Control how pricing fields like MSRP and regular Price are synchronized to your sales channels. You can apply custom rules, percentage adjustments, or fixed currency overrides to optimize your profit margins per channel.',

  category:
    'Select the precise target category for your items. Proper category mapping ensures your products appear in the correct marketplace search filters and inherit all required channel attributes.',
} as const

interface SectionHeaderInfoTooltipProps {
  message: string
  id: string
}

const SectionHeaderInfoTooltip: FC<SectionHeaderInfoTooltipProps> = ({ message, id }) => (
  <OverlayTrigger
    placement="top"
    overlay={
      <Tooltip
        id={id}
        style={{
          fontSize: '12px',
          minWidth: '280px',
          maxWidth: '420px',
          whiteSpace: 'pre-line',
          textAlign: 'left',
        }}
      >
        {message}
      </Tooltip>
    }
  >
    <span className="section-header-info-tooltip d-inline-flex align-items-center">
      <i
        className="bi bi-info-circle section-header-info-tooltip__icon"
        role="img"
        aria-label="More information"
      />
    </span>
  </OverlayTrigger>
)

interface SectionHeaderLabelProps {
  title: ReactNode
  tooltip?: string
  tooltipId: string
  required?: boolean
  requiredVariant?: 'sm' | 'spell'
  className?: string
}

export const SectionHeaderLabel: FC<SectionHeaderLabelProps> = ({
  title,
  tooltip,
  tooltipId,
  required = false,
  requiredVariant = 'sm',
  className,
}) => (
  <span className={clsx('section-header-label', className)}>
    <span className="section-header-label__title">{title}</span>
    {tooltip ? <SectionHeaderInfoTooltip id={tooltipId} message={tooltip} /> : null}
    {required ? (
      <span
        className={clsx(
          'section-header-label__required',
          requiredVariant === 'spell' && 'section-header-label__required--spell'
        )}
      >
        (Required)
      </span>
    ) : null}
  </span>
)

export default SectionHeaderInfoTooltip
